import { useEffect, useMemo, useState } from "react";
import { Search, Plus, UtensilsCrossed, ArrowRight } from "lucide-react";
import { QrMedia } from "../public/qr/QrMedia";
import { Quantity, Veg, AllDishesMark } from "../public/qr/QrMenu";
import { money } from "./orderApi";
import { Stars } from "./ItemCards";

const FoodCard = ({ it, qty, onOpen, onAdd, onQty, onCart }) => {
  const choices = !!(it.variants?.length || it.addons?.length || it.is_combo);
  const price = it.variants?.length ? Math.min(...it.variants.map((v) => v.price)) : it.base_price;
  const offer = it.offer_price != null && !it.variants?.length ? it.offer_price : null;
  const add = () => (choices ? onOpen(it) : onAdd(it));
  return (
    <article className={`qr-food-card ${it.is_available ? "" : "opacity-60"}`} data-testid={`menu-item-${it.id}`}>
      <button type="button" className="qr-food-photo-button" onClick={() => onOpen(it)} aria-label={`View ${it.name}`} data-testid={`menu-item-photo-${it.id}`}>
        <QrMedia src={it.image_url} alt={it.name} className="qr-food-photo" />
      </button>
      <div className="qr-food-info">
        <div className="qr-food-heading">
          <button type="button" className="qr-food-name" onClick={() => onOpen(it)} data-testid={`menu-item-name-${it.id}`}>{it.name}</button>
          <Veg veg={it.veg} />
          {it.is_combo && <span className="qr-customizable">Combo</span>}
        </div>
        <Stars rating={it.rating} id={it.id} />
        {it.description && <p className="qr-food-description">{it.description}</p>}
        <div className="qr-food-bottom">
          <div className="qr-food-price" data-testid={`menu-item-price-${it.id}`}>{it.variants?.length > 0 && <small>From </small>}
            {offer != null ? <><s style={{ color: "#9ca3af", fontWeight: 500, marginRight: 6 }}>{money(price)}</s>{money(offer)}</> : money(price)}</div>
          {!it.is_available ? <span className="qr-unavailable" data-testid={`menu-item-unavailable-${it.id}`}>{it.unavailable_reason === "out_of_stock" ? "Out of stock" : "Unavailable"}</span>
            : qty > 0 ? <Quantity id={`menu-item-${it.id}`} value={qty} minus={() => onQty(it, -1, onCart)} plus={() => (choices ? onCart() : onQty(it, 1))} />
            : <button type="button" className="qr-add-button" onClick={add} data-testid={`menu-add-${it.id}`}>ADD <Plus size={14} /></button>}
        </div>
        {offer != null && <span className="qr-customizable" style={{ color: "var(--qr-red)" }} data-testid={`menu-item-offer-${it.id}`}>{it.offer_name || "Offer"}</span>}
        {choices && <span className="qr-customizable">Customizable</span>}
      </div>
    </article>
  );
};

export const MenuBrowser = ({ menu, cart, onOpenItem, onCart, initialCat = "all", onCategoryChange, initialQuery = "" }) => {
  const [cat, setCat] = useState(initialCat);
  useEffect(() => { setCat(initialCat); }, [initialCat]);
  const [q, setQ] = useState("");
  useEffect(() => { if (initialQuery) setQ(initialQuery); }, [initialQuery]); // eslint-disable-line react-hooks/exhaustive-deps
  const [visible, setVisible] = useState(40);
  useEffect(() => { setVisible(40); }, [q, cat]);
  const cats = menu?.categories || [];
  const qtyOf = useMemo(() => { const m = {}; for (const l of cart.lines) m[l.item_id] = (m[l.item_id] || 0) + l.qty; return m; }, [cart.lines]);
  const items = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const all = [...(menu?.items || []), ...(menu?.combos || []).map((c) => ({ ...c, is_combo: true, base_price: c.combo_price, is_available: true }))];
    return all.filter((i) => (cat === "all" || i.category_id === cat) && `${i.name} ${i.description || ""}`.toLowerCase().includes(needle));
  }, [menu, q, cat]);
  const quickAdd = (it) => cart.add({ item_id: it.id, name: it.name, image_url: it.image_url, variant_name: null, addon_ids: [], qty: 1, unit: it.offer_price ?? it.base_price });
  const selectCat = (id) => { setCat(id); onCategoryChange && onCategoryChange(id); setTimeout(() => document.querySelector("[data-testid='menu-items']")?.scrollIntoView({ behavior: "smooth", block: "start" }), 50); };
  const changeQty = (it, delta, fallback) => {
    const idxs = cart.lines.map((l, i) => (l.item_id === it.id ? i : -1)).filter((i) => i >= 0);
    if (idxs.length !== 1) return fallback ? fallback() : onCart();
    cart.setQty(idxs[0], cart.lines[idxs[0]].qty + delta);
  };

  return (
    <div data-testid="menu-browser">
      <div className="qr-search"><Search size={18} /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search for dishes…" aria-label="Search dishes" data-testid="menu-search" /></div>
      <div>
        <nav className="qr-home-categories" aria-label="Menu categories" data-testid="category-strip" style={{ gridTemplateColumns: "repeat(4, minmax(0, 1fr))" }}>
          <button type="button" className={cat === "all" ? "selected" : ""} onClick={() => selectCat("all")} aria-pressed={cat === "all"} data-testid="category-chip-all" style={cat === "all" ? { color: "var(--qr-red)", fontWeight: 800 } : undefined}>
            <span className="qr-home-category-photo qr-category-all" style={cat === "all" ? { boxShadow: "0 0 0 3px var(--qr-red)" } : undefined}>{menu?.all_items_appearance ? <AllDishesMark appearance={menu.all_items_appearance} size={24} className="" /> : <UtensilsCrossed size={24} />}</span><span>All dishes</span>
          </button>
          {cats.map((c) => (
            <button key={c.id} type="button" className={cat === c.id ? "selected" : ""} onClick={() => selectCat(c.id)} aria-pressed={cat === c.id} data-testid={`category-chip-${c.id}`} style={cat === c.id ? { color: "var(--qr-red)", fontWeight: 800 } : undefined}>
              <QrMedia src={c.image_url} alt={c.name} className="qr-home-category-photo" /><span>{c.name}</span>
            </button>
          ))}
        </nav>
        <section className="qr-menu-section" data-testid="menu-items">
          <div className="qr-section-heading"><h2 data-testid="menu-category-heading">{cat === "all" ? "Our menu" : cats.find((c) => c.id === cat)?.name || "Our menu"}</h2><span data-testid="menu-result-count">{items.length} items</span></div>
          <div className="qr-food-grid">{items.slice(0, visible).map((it) => <FoodCard key={`${it.is_combo ? "c" : "i"}-${it.id}`} it={it} qty={qtyOf[it.id] || 0} onOpen={onOpenItem} onAdd={quickAdd} onQty={changeQty} onCart={onCart} />)}</div>
          {items.length > visible && <button type="button" className="qr-text-button qr-wide" onClick={() => setVisible((n) => n + 40)} data-testid="menu-load-more">More dishes <ArrowRight size={16} /></button>}
          {!items.length && <p className="qr-empty" data-testid="menu-empty">{cats.length ? "No dishes found." : "This outlet has not published a menu yet."}</p>}
        </section>
      </div>
    </div>
  );
};
